import { rem } from '../tokens/spacing';
import { darkAppearance, highContrastAppearance, lightAppearance } from './appearances';
import { getChartOverrides } from './chartDerivation';
import { getDensityTokens } from './densities';
import { getTypographyTokens } from './typographyVars';
import type { Appearance, BrandConfig, Density, Theme } from './types';

/** Base radii in px (root 10px — rem() matches tokens/radii). */
const BASE_RADIUS_PX = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
} as const;

const RADIUS_SCALE: Record<NonNullable<BrandConfig['radius']>, number> = {
  none: 0,
  sm: 0.75,
  md: 1,
  lg: 1.25,
  xl: 1.5,
};

function getRadiusTokens(brand: BrandConfig): Record<string, string> {
  const tier = brand.radius ?? 'md';
  if (tier === 'none') {
    return {
      '--ui-radius-sm': rem(0),
      '--ui-radius-md': rem(0),
      '--ui-radius-lg': rem(0),
      '--ui-radius-xl': rem(0),
      '--ui-radius-full': rem(0),
    };
  }

  const mult = RADIUS_SCALE[tier];
  return {
    '--ui-radius-sm': rem(Math.max(0, Math.round(BASE_RADIUS_PX.sm * mult))),
    '--ui-radius-md': rem(Math.max(0, Math.round(BASE_RADIUS_PX.md * mult))),
    '--ui-radius-lg': rem(Math.max(0, Math.round(BASE_RADIUS_PX.lg * mult))),
    '--ui-radius-xl': rem(Math.max(0, Math.round(BASE_RADIUS_PX.xl * mult))),
    '--ui-radius-full': rem(BASE_RADIUS_PX.full),
  };
}

function resolveAppearanceTokens(appearance: Appearance, brand: BrandConfig): Record<string, string> {
  switch (appearance) {
    case 'light':
      return lightAppearance(brand);
    case 'dark':
      return darkAppearance(brand);
    case 'high-contrast':
      return highContrastAppearance(brand);
    default: {
      const _exhaustive: never = appearance;
      return _exhaustive;
    }
  }
}

export function createTheme(
  brand: BrandConfig,
  appearance: Appearance = 'light',
  density: Density = 'comfortable',
): Theme {
  const appearanceTokens = resolveAppearanceTokens(appearance, brand);
  const densityTokens = getDensityTokens(density);
  const radiusTokens = getRadiusTokens(brand);

  const typographyTokens = getTypographyTokens(brand);
  const chartOverrides = getChartOverrides(brand);

  const cssVars: Record<string, string> = {
    ...appearanceTokens,
    ...densityTokens,
    ...radiusTokens,
    ...typographyTokens,
    ...(chartOverrides ?? {}),
  };

  return {
    brand,
    appearance,
    density,
    tokens: cssVars,
    cssVars,
  };
}
